import { stampBodies } from "./regen";
import { beltMirrorStale, crushBeltUnder, syncBeltMirror, wreckBelt } from "./industry/beltParts";
import { kinglessGrace } from "./campaign/despair";
import { blowStep, shovePiece, standable } from "./chess/blow";
import { lanceSteps, lancesFrom, withinHorn } from "./unicorn";
import { clearLine } from "./chess/plow";
import { dragSquare } from "./chess/clasp";
// The command log is the save format, the undo mechanism, the replay
// mechanism, the telemetry substrate and (from Phase 4) the network protocol.
// Every state change goes through `applyCommand` — nothing may mutate
// GameState directly.

import {
  type BoardEdge,
  CLASS_FODDER,
  type Color,
  fileOf,
  KING,
  KNIGHT,
  opposite,
  PAWN,
  pieceColor,
  type PieceType,
  pieceType,
  QUEEN,
  rankOf,
  type Side,
  square,
  makePiece,
  knightReaches,
  REAIM_RADIUS,
  COORD_MAX,
  COORD_MIN,
  EAST,
  NORTH,
  SOUTH,
  WEST,
  STRIDE,
  WHITE
} from "./board";
import { COAL, depositFault, FUEL, METAL, type MaterialLine } from "./materials";
import { type DisguiseBreak, disguiseBroken } from "./campaign/disguise";
import { canBeHurt, legalMoves} from "./chess/moveGen";
import { makeMove} from "./chess/makeMove";
import { type GameStatus, gameStatus, repetitionKey} from "./chess/status";
import {
  bufferCapacity,
  pickUpDropped,
} from "./industry/machines";
import { detonate, isNuclearAt } from "./blast";
import { overwatch } from "./gunfire";
import {
  claimStock,
  hurtStructure,
  touches,
  wreckStructure
} from "./industry/structures";
import {
  cloneIndustry,
  hasBelt,
  addHeap,
  addToBuffer,
  createIndustry,
  type DepositRow,
  type Direction,
  COBBLE_UNIT,
  dropOnGround,
  type IndustryState,
  makeUnit,
  migratePiece,
  preachedAtOn,
  seedIndustry,
  setPreachedAt,
  setBelt,
  type StructureKind,
  setHeap,
  stripHoldings,
  structureAt,
  withGrade,
} from "./industry/state";
import {
  isCastle,
  isDrag,
  isEnPassant,
  isStructureBlow,
  isStructureCapture,
  type Move,
  moveCapturedType,
  moveFrom,
  movePromotion,
  moveTarget,
  moveTo,
  moveToUci,
  isBash,
  isRingOut
} from "./move";
import {
  areEnemies,
  blowOf,
  disguisedAs,
  setDisguise,
  growSides,
  FACTIONS,
  KING_FACTORY,
  parseFen,
  pieceAt,
  pieceIdAt,
  type Position,
  setCells,
  setFacing,
  hasCell,
  setDemand,
  setKingKind,
  setPiece,
  setPromotionOwner,
  setPromotionUses,
  pieceClassAt,
  setEmpowered,
  spendEmpowered,
  variantAt,
  clonePosition,
  needsPieceIds,
  stampPieceIds,
  START_FEN,
  reframePlating,
  armourLeft,
  promotesOn
} from "./position";
// The board a game opens on, expanded from its name. A table rather than a
// board on the wire — see `maps.ts`.
import { type BoardMapId, boardMap } from "./maps";
import { placeMapZones, placeZones, type ZoneRow } from "./zones";
import { growRegionZones, layGeneratedMap, MAP_GENS } from "./mapgen";
import { syncGear } from "./campaign/equipment";
import { settleArtisan } from "./campaign/artisan";
import { settleDefiance } from "./campaign/defiance";
import { payHouseOfCards } from "./campaign/houseOfCards";
import { artifactMirrorStale, syncArtifactMirror } from "./industry/artifactMirror";
import { overflowStale, settleOverflow } from "./industry/overflow";
import { furnaceBeside } from "./campaign/sacrifice";
import {
  type Grade,
  pieceVariant,
  RULES,
  SURRENDER_VALUE,
  PIECE_PLATING
} from "./rules";
import { addTally, type StageTally, tallySide } from "./tally";
import {
  isDefaultRuleset,
  normalizeRuleset,
  type Ruleset,
  type RulesetChoice
} from "./ruleset";
// A value, not just the type. Safe from the cycle rule the header describes:
// `cards.ts` takes `GameState` with `import type`, which is erased.
import { CARD_TABLE, type CardId} from "./campaign/cards";
import { type PowerId} from "./campaign/powers";
import type { LevelSetup, ObjectiveState} from "./campaign/level";
import { openObjectives} from "./campaign/objectives";
// Hard Mode's one reading (§26 F13). A value, and safe from the cycle rule the
// header describes for the same reason `cards.ts` is: `mode.ts` takes
// `RunState` with `import type`, which is erased.
import { normalizeRunMode, type RunMode } from "./campaign/mode";
import { dealHand, payForMonarchs } from "./campaign/deck";
import {
  type GambitReport,
  type TickReport
} from "./tick";
import { endTurn, freeMoveRefusal, requireMove, spendMove } from "./clock";
import { boostFuel, boostRefusal } from "./industry/empower";
import { drawNetworkFuel } from "./industry/power";
import {
  IllegalCommandError,
  claimPieceAtAltar,
  claimPieceToBearer,
  cloneState,
  damage,
  findLegalMove,
  placeDeposits,
  spillPiece,
  openRegion,
  vacuumAlong,
  requireKingSafe,
  requireOwnTurn,
} from "./reduce";
// Re-exported so every existing import site keeps working: the split is
// about where the code lives, not about what this module offers.
export { IllegalCommandError, aloneWithKing, findLegalMove } from "./reduce";
import { reduceIndustry} from "./industry/reduce";
import { reduceCampaign} from "./campaign/reduce";

export interface CardInstance {
  id: number;
  card: CardId;
}

/** A trophy (§31 A2): what an enemy Monarch's destruction paid, fixed when earned. */
export interface Trophy {
  id: number;
  /** The three cards it offers, rolled off `MONARCH_PACK` when it was earned. */
  offer: CardId[];
}

export interface RunState {
  /** Which side the player is. 0 today; named rather than assumed. */
  side: Color;
  /**
   * A camp that has been *bought* and has not landed yet.
   *
   * A front used to close and open in one command: `next-stage` swept the
   * beaten army and put the next one down in the same beat. That is why a
   * cleared front leaves the board **frozen** — the beaten side is on the clock
   * with no move, so the player cannot take a turn until the camp arrives, and
   * the branch picker has to be undismissable for exactly that reason.
   *
   * This is the seam between the two halves. `clear-front` sweeps the army and
   * sets this; the board goes back to `playing` and the player gets turns to
   * spend before the next fight arrives. `until` is the round the camp may land
   * on, compared against `Position.round` (§31 T).
   *
   * Optional, so a run that never gets one hashes and replays exactly as it
   * did — which is what leaves the undelayed path untouched.
   */
  pending?: { until: number };
  /**
   * The spawn clock: when the next camp lands on its own (§26 F10).
   *
   * Only ever set under `ruleset.spawn === "clock"`, where camps arrive every
   * `RULES.spawnEvery` rounds whether or not the last one is beaten. Armed by
   * `armSpawnClock` in `endTurn` once an enemy first stands on the board — the
   * war is the anchor, or the opening solo phase would be on the clock — and
   * cleared by `next-stage` when a camp lands, so the next `endTurn` re-arms
   * it and the interval runs landing to landing. `until` reads exactly as
   * `pending.until` does and is compared in the same place (`waveDue`).
   *
   * Optional and never set in the shipped `"defeat"` mode, so every existing
   * run carries nothing and replays exactly as it did — `pending`'s shape,
   * unhashed for `pending`'s reason.
   */
  spawn?: { until: number };
  /** Every card owned, in acquisition order. */
  deck: CardInstance[];
  /** Instance ids drawn for this turn. */
  hand: number[];
  /** Next instance id. In state, so a replay assigns the same ones. */
  nextCardId: number;
  /** Played cards, for the run summary and the scorecard. */
  spent: CardId[];
  /**
   * Board tiles left to place, from Free Space.
   *
   * A **banked count**, not a card that pastes five squares at once: paving is
   * a decision per square — where the ground goes is the whole card — and a
   * gesture that collects five targets before anything happens is one the
   * player cannot change their mind partway through.
   *
   * Optional, so every run that has never seen the card carries nothing and
   * hashes exactly as it did before it existed.
   */
  tiles?: number;
  /** The level being played, regions and objectives included. */
  level?: LevelSetup;
  /** Regions already on the board, by id. */
  revealed: string[];
  /** Objective progress, in order. */
  objectives: ObjectiveState[];
  /**
   * Structure tokens in hand, in acquisition order (§26 F6).
   *
   * What replaced `run.powers`: a power is no longer *held*, it is **on while
   * a structure of its kind stands under your control** (`hasPower`), and a
   * token is the unplaced structure — a dev grant or a scenario's until A1's
   * Critical Spark (§31 R1). Spent by `place-structure`. A count a move can spend, so it
   * is in the hash, `tiles`' argument exactly; optional, so every run that has
   * never held one hashes as it did before tokens existed.
   */
  tokens?: StructureKind[];
  /**
   * Extra factory ticks per side, at the end of each of that side's moves.
   *
   * **Indexed by side, unlike `titan`**, because the thing that grants these is
   * a structure the enemy can capture (§26 A2) — so which army holds them is a
   * fact about the board rather than about whose run it is. See
   * `campaign/ticks.ts`.
   *
   * Optional, so every run that has never held one carries nothing and hashes
   * exactly as it did before this existed.
   */
  ticksPerSide?: number[];
  /**
   * Off-Turn Production per side (§31 T3): at each round rollover a side with
   * N runs N ticks per enemy faction alive last round. Base nought; nothing
   * grants it yet (card C4, artifact A10 will). `withOffTurn` is the writer and
   * owns `ticksPerSide`'s shape — dense, trimmed, dropped at nought.
   */
  offTurnPerSide?: number[];
  /**
   * One-shot Off-Turn Production per side (§31 C4, The Enemy is at the
   * Gates): added to `offTurnPerSide` at the next round rollover, then
   * dropped. `withOffTurnOnce` is the writer, `withOffTurn`'s shape. Hashed.
   */
  offTurnOnce?: number[];
  /**
   * Trophies held (§31 A2): one per enemy Monarch destroyed, each with its
   * three cards rolled when earned. Exchanged (`claim-trophy`) or kept
   * (`keep-trophy`, scored in `tally.trophies`). Hashed; absent when none.
   */
  trophies?: Trophy[];
  /** Next trophy id. In state, so a replay assigns the same ones. */
  nextTrophyId?: number;
  /**
   * Titan Relics each side has placed, held or lost (§31 A5): the price
   * climbs `RULES.relicStep` marble per one. Indexed by side; hashed; absent
   * until the first.
   */
  relicsBuilt?: number[];
  /**
   * The last Oil-Stone Scepter seizure (§31 A4): `by` plays `target`'s turn
   * in round `round`; seized in round `at`, so the next may come at
   * `at + RULES.seizeEvery`. Hashed; absent until the first. Read through
   * `seizedBy` (`campaign/scepter.ts`).
   */
  seizure?: { by: Side; target: Side; round: number; at: number };
  /**
   * Hard Mode: which game this run is playing (§26 F13).
   *
   * **Its own fact rather than a reading of the opponent's `Level`**, though
   * one picker sets both. A `Level` is a per-camp skill that *climbs* through
   * a run (`levelForStage`) and is chosen per side by a scenario; this is a
   * run-wide constant in force from turn one — despair's grace period, the
   * spawn clock's interval, and how strong a camp arrives. Hanging those off
   * the skill would switch them on mid-run and leak them into scenarios.
   *
   * Optional, and **only ever stored when hard** — `tiles`' and `tokens`'
   * shape — so every existing run and every normal one carries nothing and
   * hashes byte-identically to what it did before this existed. Read through
   * `campaign/mode.ts`, never off the field, so "absent means normal" is
   * decided in one place.
   */
  mode?: "hard";
}

export interface GameState {
  position: Position;
  industry: IndustryState;
  history: Move[];
  repetitions: Map<string, number>;
  status: GameStatus;
  /** What the last tick did — the UI narrates from this. */
  lastTick?: TickReport;
  /**
   * Single player only, and **its presence is what makes a game a run**.
   *
   * One optional field rather than a flag plus a payload: there is then no
   * state in which the game thinks it is a run but has no deck, and no third
   * place for "is this single player" to be answered differently. The server
   * refuses any command that would create one.
   */
  run?: RunState;
  /** Set when a Gambit resolved on the last turn, for the UI to announce. */
  lastGambit?: GambitReport;
  /**
   * What the player has done since the last stage boundary (`tally.ts`).
   *
   * Stored rather than derived, which is the exception here and argued for in
   * that file: a stage ends by sweeping the beaten army off the board, so the
   * position cannot answer "what did that front cost them" the moment it is
   * worth asking. Not in the hash.
   */
  tally?: StageTally;
  /** The finished tally of the stage just cleared — what the round card reads. */
  lastStage?: StageTally;
  /**
   * Every front the run has *finished*, added together.
   *
   * The round card is about one front and the run-over card is about the run,
   * and until this existed there was only the first number: the ending read
   * `tally`, so a run that had cleared three fronts reported what had happened
   * since the last boundary. Lose a turn after clearing one and the card said
   * you had made two moves and dug nothing.
   *
   * Accumulated at the **rollover** rather than beside every `addTally`, so
   * there is one site and the two counts cannot drift. It excludes the front in
   * progress, which the reader adds — `tally` is still the live one.
   *
   * Not in the hash, for the same reason `tally` is not.
   */
  runTally?: StageTally;
  /**
   * How many fronts have turned over — the round the game is on, minus one.
   *
   * **In state because a run is saved as a snapshot.** It used to be counted
   * off the command log (`next-stage` commands in it), which is right for
   * everything the log *is* the format for — and a run is the one thing it is
   * not (`game/runSave.ts`). `GameStore.restore` lands a snapshot with an
   * **empty log**, so every reader of that count answered nought on the reload:
   * a run five fronts deep came back at stage one, which is not a cosmetic
   * number. It picks the camp (`stageOffers` rolls `streamFor(seed, stage)`),
   * the army style (`styleForStage`), the difficulty the run has climbed to
   * (`levelForStage`) and what the
   * defeat plate says. The board came back; the round did not.
   *
   * Maintained at the rollover in `next-stage`, the one moment a front
   * actually finishes, so it cannot drift from the count it replaces —
   * `campaign.test.ts` plays a headless run and asserts the two agree.
   *
   * **Not run-only**: a scenario clears fronts and takes waves exactly as a run
   * does (`stageTurnsOver` is `!run || …`), so this cannot live on `RunState`.
   *
   * Absent until the first front closes, which is what keeps every hash, every
   * published perft count and every save written before it existed unmoved.
   */
  stage?: number;
  /**
   * Which board this game was opened on (`maps.ts`).
   *
   * Remembered for the reason `seed` is: **"New game" has to mean the same
   * board again**, and the only other place that answer could come from is the
   * shell that launched it — which the player who *joined* the room never had,
   * and which a reload throws away. Two clients would then disagree about what
   * that button does on a board they agree about.
   *
   * Absent is the classic board, so every log written before this existed
   * opens exactly as it did.
   */
  map?: BoardMapId;
  seed: number;
  /**
   * Which game the players agreed to play (`ruleset.ts`, §26 F1).
   *
   * Stored for `map`'s reason — "New game" has to mean the same game again,
   * and the player who joined never had the menu that chose it. **Present only
   * when not the default**, so every log, save and hash written before it
   * existed is unmoved; read through `rulesetOf`, never directly.
   */
  ruleset?: Ruleset;
}

// Belt commands carry their `owner` rather than inferring it from whose turn
// it is, because the server binds that field to the sender's seat
// (server/rooms.ts) — without it, a client could build as its opponent. The
// clock is a separate gate and `requireBuilder` holds it: since §29 U1 every
// one of these waits for the side to move, so `owner` must equal it too.
// Construction still costs no tempo; what it costs is that it happens on your
// own turn, which is what gives undo a turn to take back.
export type Command =
  /**
   * `heaps` seeds scrap for a single-player scenario. Part of game setup
   * rather than its own command, because anything that can create material
   * out of nothing has to be confined to "start a game" — `new-game` already
   * resets everything, so it grants no power a player did not have.
   */
  | {
      type: "new-game";
      fen?: string;
      seed?: number;
      /**
       * Which board to open on (`maps.ts`). Absent is the classic sixty-four.
       *
       * **A name, because the board itself may not cross the wire.** `cells`,
       * `heaps` and `belts` are all refused by the server — between them they
       * conjure ground and a factory nobody built — so a two-player game could
       * only ever be played on the chess opening. An id conjures nothing: both
       * ends expand the same table, so the server validates it by knowing it,
       * and an unknown one is refused rather than quietly opening something
       * else.
       */
      map?: BoardMapId;
      heaps?: [number, number][];
      /**
       * Finite mineable seams: square, line, rung, amount (§26 F3).
       *
       * Where `heaps` is the scrap a corpse leaves, a deposit is what the map
       * was *generated* with, and it carries a line — which is the only source
       * stone and the specials have. Set-piece only, refused by the server
       * beside `heaps`: it conjures material nobody dug.
       */
      deposits?: readonly DepositRow[];
      /**
       * Zones (§31 D2): kind and rectangle; what grows in each is the seed's
       * (`placeZones`). Set-piece only, refused by the server beside
       * `deposits` — it conjures ore and loose material. A map's own zones
       * cross as its name.
       */
      zones?: readonly ZoneRow[];
      /**
       * How many moves each side plays before the clock passes. Defaults to
       * one, which is chess. In the opening command rather than a constant
       * because it is a property of *this game*, and a replay that did not
       * carry it would diverge on the first move.
       */
      movesPerTurn?: number;
      /**
       * Start a run: single player only, and the server refuses it.
       *
       * A deck of card ids to begin with. An empty array still makes it a run
       * — `run` being present is the whole test — which is what a real level
       * will want, since a run starts with nothing and earns its deck.
       */
      /**
       * Belt parts and material already on the board.
       *
       * Set-piece only, like `heaps`, and refused by the server for the same
       * reason: they conjure a factory nobody built. They go in the opening
       * command rather than being poked into the state afterwards so that a
       * scenario is still a log, and still replays.
       */
      belts?: readonly [square: number, direction: Direction, owner: Color][];
      /** `line` absent is metal; a rung no line has is refused (§31 S2). */
      stock?: readonly [square: number, grade: number, count: number, line?: MaterialLine][];
      /**
       * Refined material on the *ground*, where `heaps` is scrap and `stock` is
       * in a piece's hands. See `newGame`'s own note.
       *
       * Single-player only, refused by the server beside `heaps`.
       */
      metal?: readonly [square: number, grade: number, count: number][];
      /**
       * Coal lying loose on the ground, as square / count (§31 M2) — the fuel
       * a board's metal is raised with. Set-piece only, refused by the server
       * beside `heaps`: it conjures material nobody dug.
       */
      coal?: readonly [square: number, count: number][];
      /**
       * Which faction each side is (`KING_KIND`), indexed by side.
       *
       * Declared in the opening command because a replay that did not carry
       * it would derive a different game. Omitted sides are factory armies,
       * the only faction today (§31 R2); an unknown id is refused. Not refused
       * by the server: it conjures nothing.
       */
      kingKinds?: readonly number[];
      /**
       * Squares worth going to (`Position.objectives`).
       *
       * A sensor for the planners rather than a rule — nothing about movement
       * or the economy reads it. Board setup like the rest of this, so it
       * rides in the opening command and replays.
       */
      objectives?: readonly number[];
      /**
       * A flow map per side, in `DEMAND_SLOTS` order (`rules.ts`).
       *
       * Where this army sends its material, as against everybody else's. It is
       * board setup like `kingKinds` — the reducer routes, so a table only one
       * client had would desync at the first hand-off — and it rides here for
       * the same reason: the opening command is the one place an army's rules
       * may be declared, so it replays and hashes like any other.
       *
       * A hole, a short row or a missing side keeps the default, so naming one
       * army's map says nothing about anybody else's.
       */
      demand?: readonly (readonly number[] | undefined)[];
      /**
       * Armies past the two a FEN can describe.
       *
       * **FEN is two-sided and the rules are not** — `Side` is three bits, so
       * up to eight armies, and `alliances` defaults every one of them to an
       * alliance of its own. What was missing was a way to *open* with more
       * than two: `next-stage` and `dev-grow` conjure armies mid-game and both
       * are gated on things a fresh board has not done yet.
       *
       * Placed after the FEN, so a scenario can open from an empty board and
       * describe all four corners in one table rather than two here and two
       * over there. Set-piece only, refused by the server beside `heaps` —
       * not because a four-army game is unsound, but because seats and the
       * network protocol are still two-sided (PLAN §5.5).
       */
      armies?: readonly [
        square: number,
        side: Side,
        type: PieceType,
        facing: Direction
      ][];
      /**
       * Ground beyond the sixty-four a FEN can describe.
       *
       * **Added to the board, never replacing it**, because a FEN is still
       * what puts the pieces on it: a scenario opens on the classic squares
       * and then says how far the ground runs past them. That is what lets a
       * board be *padded* — armies in their ordinary places with room behind
       * them for the scrap they will spend the game arguing over.
       *
       * The only other ways ground exists are a level's regions and a stage
       * boundary, and both are things that happen *during* a game. This is the
       * board a game opens on, so it rides in the opening command and replays
       * and hashes like the rest of the setup — `bounds` is hashed always, and
       * membership too once the shape is ragged.
       *
       * Set-piece only, refused by the server beside `heaps` and `armies`: it
       * is the same class of thing, a board nobody played their way onto.
       */
      cells?: readonly number[];
      /**
       * Which alliance each side belongs to, indexed by side.
       *
       * An id, not a flag: sides sharing a number are allies, so `[0,0,1,1]`
       * is two teams of two and `[0,1,2,3]` — the default — is everybody for
       * themselves. Alliance is already state (`Position.alliances`) and
       * already travels; what was missing was a way to *open* with one, since
       * nothing else sets it.
       *
       * Set-piece only for the same reason `armies` is: a two-sided protocol
       * has nowhere to put a third army, and allying the only two sides in a
       * network game means neither can take anything.
       */
      alliances?: readonly number[];
      run?: readonly CardId[];
      /** Start an authored level. Single player only; the server refuses it. */
      level?: LevelSetup;
      /**
       * Which game to play (`ruleset.ts`, §26 F1): any subset of the dials,
       * an omitted one being the default. In the opening command because a
       * replay that did not carry it would derive a different game from the
       * same log — `kingKinds`' argument exactly, and like `kingKinds` it is
       * **not refused by the server**: it conjures nothing, and "we are both
       * playing the spawn clock" is a legitimate two-player agreement. An unknown
       * value is refused, never defaulted, for the map's reason.
       */
      ruleset?: RulesetChoice;
      /**
       * Hard Mode, for a run or a level (§26 F13).
       *
       * The run-wide half of the easy/normal/hard picker — the skill half is
       * `Level`, which never crosses into the rules at all. It rides in the
       * opening command for `ruleset`'s reason: `armSpawnClock` reads it from
       * inside `endTurn`, which is `src/engine/rules/` and cannot reach the shell, so
       * a replay that did not carry it would derive a different game from the
       * same log.
       *
       * Absent and `"normal"` are the same thing and store nothing; an unknown
       * value is **refused, never defaulted**, the map's rule. Ignored without
       * a run or a level, since there is no run for it to be the constitution
       * of — and refused by the server outright, a run being single-player.
       *
       * Typed narrowly and checked widely: the type promises the vocabulary
       * and the wire promises nothing, so `normalizeRunMode` tests strings.
       */
      mode?: RunMode;
    }
  /**
   * The first half of a stage boundary: sweep the beaten army, land nothing.
   *
   * Same three guards `next-stage` carries and for the same reason — this
   * destroys an army — but it conjures nothing, which is the whole difference.
   * What it leaves is a playable board and `run.pending`: the camp is bought
   * and due in `delay` turns, and until then the player has the front to
   * themselves.
   *
   * **A delay of nought is not this command.** With no wait to serve there is
   * nothing between the two halves and the old single beat is exactly right,
   * so the driver sends `next-stage` as it always did. That is what keeps the
   * undelayed path — every existing run, every replay, the headless rig —
   * byte-identical.
   */
  | {
      type: "clear-front";
      /** The beaten side, whose army this sweeps. */
      side: Side;
      /** Turns to wait before the camp may land. At least one. */
      delay: number;
    }
  /**
   * Single player's stage boundary: the beaten side's army is cleared away as
   * scrap and a fresh force takes the field, so beating the opponent opens the
   * next front instead of ending the game. Only legal once that side has *no
   * move left* — mate or stalemate — which is what stops it being a general
   * "conjure an army" command, and local only: the server refuses it outright.
   */
  | {
      type: "next-stage";
      side: Side;
      /** Squares to add to the board — the new ground the wave arrives on. */
      cells?: number[];
      /**
       * Zones the board grows by (§31 D3, `growZones`): each rectangle becomes
       * ground where there was none, its contents grown off the seed. Absent
       * is the stage as it always was.
       */
      zones?: readonly ZoneRow[];
      /** How many armies are in play once this lands, if it introduces one. */
      sides?: number;
      /**
       * Promotion squares the new region reveals, as `[square, uses]`.
       *
       * Ordinary promotion is off once the board has expanded, so this is what
       * replaces it: specific ground a pawn can be *sent to*, worth fighting
       * over because it runs out. Single player only, like the rest of this
       * command — it changes the board, and the server refuses the lot.
       */
      promotion?: [number, number][];
      pieces: [number, number][];
      /**
       * The camp's own industry, and **this is the half that makes an authored
       * camp itself.**
       *
       * A camp used to arrive as ground and a formation, which is all a
       * *procedural* camp has ever been — `ai/formations.ts` picks pieces out
       * of a table and puts them on a shape. An authored one is written in
       * Tiled as a room, and a room is allowed a seam of ore, a stock of
       * refined metal, pawns turned to face the way the author drew them and
       * buildings standing on the floor. Landing one through a command that
       * carries none of that lands a different camp: six pieces in a field.
       *
       * Every one of these is exactly what `RegionSetup` carries and what
       * `openRegion` places, in the same order and with the same guards — the
       * two paths put a region on the board and must not drift, which is the
       * argument that made `openRegion` shared in the first place.
       */
      /**
       * Give the board back instead of taking it away.
       *
       * The other kind of stage, for a board where no camp will fit: a map
       * with nowhere to put a front would leave the run stalled. `seed` is
       * the edge square nearest
       * your army (`growthSeed`) and `depth` is how far the ground carries.
       */
      grow?: { seed: number; depth: number };
      heaps?: [square: number, amount: number][];
      /**
       * Seams generated with the camp's ground (§26 F3), as `[square, line,
       * grade, amount]`. Filtered to the ground the camp actually opened,
       * exactly as `heaps` and `metal` are — one on an existing square would
       * litter the board the player is already playing.
       */
      deposits?: readonly DepositRow[];
      /** Refined material on the floor, as `[square, grade, count]`. */
      metal?: [square: number, grade: number, count: number][];
      /** Pawn facings, overriding the arriving side's forward. */
      facing?: [square: number, direction: number][];
      /** Buildings the camp stands on, as `[square, kind, owner]`. */
      structures?: [square: number, kind: number, owner: Side][];
      /**
       * The faction each arriving side flies, as `[side, kingKind]` (§31 G3,
       * `factionForStage`). Absent is the factory army; an unknown id is
       * refused (`FACTIONS`).
       */
      kings?: readonly [side: Side, kind: number][];
      /**
       * Run a Productivity Gambit as the stage turns over, and give the
       * arriving army a turn of grace.
       *
       * Single player only, and the only place a Gambit fires there. Beating
       * an army is when you have earned a run of your factory, and it is also
       * the moment you most need one: the new camp arrives with a formation
       * already in place while your own network is wherever the last fight
       * left it.
       */
      gambit?: boolean;
    }
  /**
   * `boost` (§33 S1): the fuel source whose Empower network grants this as
   * an extra move — no pool spent, its fuel drawn, Factory Flow aside.
   */
  | { type: "move"; from: number; to: number; promotion?: PieceType; boost?: number }
  | { type: "lay-belt"; square: number; direction: Direction; owner: Color }
  /**
   * A straight run of parts from `from` to `to` along a rank or file, each
   * facing the way the line travels. One command rather than N, so that the
   * gesture and the undo unit are the same thing, and so a lane crosses the
   * wire as one message.
   */
  | { type: "lay-belt-line"; from: number; to: number; owner: Color }
  /**
   * A freehand run drawn square by square, each part aimed at the next so the
   * lane turns corners. One command per gesture, for the same reason as
   * `lay-belt-line`: the thing you drew and the thing you undo are the same.
   */
  | { type: "lay-belt-path"; squares: number[]; owner: Color }
  | { type: "rotate-belt"; square: number; direction: Direction; owner: Color }
  | { type: "remove-belt"; square: number; owner: Color }
  | { type: "toggle-belt"; square: number; owner: Color }
  /**
   * A pawn digs one unit out of the heap it stands on, and hands one unit to
   * an adjacent part. Free actions, taken as often as you like on your turn:
   * industry costs board budget, not tempo (PLAN §2, Decision 2). They exist
   * as commands rather than tick steps so the player presses something and
   * watches it happen.
   */
  | { type: "mine"; square: number; owner: Color }
  | { type: "load-belt"; square: number; owner: Color }
  /**
   * What a foundry is saving for, and which way it points what it mints.
   *
   * Machine policy, so it carries an `owner` and is bound to the seat rather
   * than to the clock — the server binds it in `rooms.ts` like every other
   * command with an owner.
   */
  | {
      type: "set-target";
      square: number;
      target: PieceType | 0;
      facing?: Direction;
      owner?: Color;
    }
  /**
   * Forge a piece. **Costs the turn**, like a move.
   *
   * Minting used to happen on the tick: set a recipe, feed the foundry, and a
   * piece appeared whenever the material added up. That made the biggest thing
   * a foundry does free, and it happened while you were looking somewhere
   * else. Spending the turn on it makes building a piece a decision with a
   * price, and puts the moment under the player's hand.
   *
   * `at` picks which touching square it lands on, for the common case where
   * more than one is free.
   *
   * **Either half may be left out, and at least one must be there.** `square`
   * is the *lead* foundry — the machine the player clicked, which is what a
   * minted pawn is pointed away from and whose standing facing applies. `at`
   * is where the piece lands. Since every foundry touching the landing square
   * splits the bill (PLAN-UI §28.4), a forge aimed at a square alone is a
   * complete instruction and is what the hotbar sends (§28 U4); the reducer
   * takes the lowest-square funder as the lead. Neither is refused.
   */
  | {
      type: "forge";
      square?: number;
      target: PieceType;
      at?: number;
      facing?: Direction;
    }
  /**
   * Put up a building (§31 S3). **Costs no move** but waits for your turn.
   *
   * `square` is **any piece of yours** and `at` is a bare square beside it;
   * the bill comes from that piece's inbox, then your stores nearest `at`
   * (`buildFunds`). It may not leave your king in check — an obstruction
   * blocks a line (§31 S1).
   */
  | {
      type: "build";
      square: number;
      at: number;
      kind: StructureKind;
      owner?: Color;
      /** A Kinetic or Cannon Turret's facing (§31 S8); absent faces its side's forward. */
      facing?: Direction;
    }
  /**
   * Take a building down. **Costs the turn**, and what is left depends on
   * whose it was — see `demolishStructure`.
   *
   * The owner's way to take one down from beside it; an enemy wrecks one by
   * moving into or through it (§31 S1).
   */
  | { type: "demolish"; square: number; at: number }
  /**
   * Order the Train Engine on `at` to `to`, forward along your own track
   * (§31 S7, `driveError`); `to === at` stops it. It runs in the ticks.
   * **Costs no move** and waits for your turn.
   */
  | { type: "drive-train"; at: number; to: number }
  /**
   * Put your piece on `from` into your Train Car on `car`, beside it (§31 S7,
   * `boardError`). Costs no move; two a car a turn; never a royal.
   */
  | { type: "board-train"; car: number; from: number }
  /**
   * Set rider `rider` (default 0) of your car on `car` down on `to`,
   * orthogonally beside it (§31 S7, `alightError`). Costs no move; two a car a turn.
   */
  | { type: "alight-train"; car: number; to: number; rider?: number }
  /**
   * Stand an artifact structure up from a token in hand (§26 F6).
   *
   * **Costs the turn like `build`**, for build's own reason: a structure takes
   * a square off the board, and removing a cell can cut the line a check comes
   * down. Unlike `build` it needs no foundry and no adjacency, so the only
   * ground rule is bare ground you hold; it pays the artifact's bill
   * (`artifactBill`, §31 A3–A5). Run-only; the server
   * refuses it with the rest of `SOLO_COMMANDS`.
   */
  | { type: "place-structure"; kind: StructureKind; at: number }
  /**
   * Dev: put a structure token in hand. `grant-card`'s shape, for the same
   * reason it exists — testing a power means standing its structure. Until
   * A1 it is the only way a run gets one (§31 R1).
   */
  | { type: "grant-token"; kind: StructureKind }
  /**
   * Which grades a store takes in and which it hands out (PLAN §14.4.2).
   *
   * Machine policy, like `set-dump` and `set-target`: free of tempo, and
   * owner-bound to the seat. Retasking a warehouse is the same class of act as
   * choosing a foundry's recipe, and the cost is in hauling material to it
   * rather than in the click. Turn-bound all the same (§29 U1).
   */
  | {
      type: "set-store-policy";
      square: number;
      grade: Grade;
      accept: boolean;
      offer: boolean;
      owner?: Color;
    }
  /**
   * A knight turns one of its pawns within `REAIM_RADIUS` (§31 P1); with
   * `step`, the pawn also walks one non-capturing square on its new facing.
   */
  | { type: "re-aim"; knight: number; pawn: number; facing: Direction; step?: boolean }
  /**
   * A Marshal spends its whole move to turn, and to turn the fodder beside it.
   *
   * **`re-aim`'s shape from the other side**: a knight points somebody else a
   * knight's move away, a Marshal points itself and reaches only what it
   * touches. `also` is a list rather than "all of them" — a half-turned
   * formation is a thing worth being able to build — and all take the same
   * facing. Illegal in check, `re-aim`'s mechanism: it would be a free pass.
   */
  | { type: "drill"; marshal: number; facing: Direction; also?: readonly number[] }
  /**
   * A Confessor spends her move to hand the piece beside her two of its own.
   *
   * Tempo moved from one piece to another, which is why she pays a whole turn
   * for it and may only do it every `RULES.empowerRounds`. The grant belongs to
   * the *piece* (`Position.empowered`), so the side's next moves have to be
   * that piece's — and it lapses with the turn, spent or not.
   */
  | { type: "empower"; confessor: number; target: number }
  /**
   * A Unicorn Knight shuffles a square and puts its horn through something.
   *
   * `step` is optional and `at` is measured from where it ends up. A command
   * and not a move: `unmakeMove` cannot restore health.
   *
   * `move` (§31 P4, a `lancesOnMove` row — the Alicorn): the
   * destination of one of its own legal moves, made first as a `move` would
   * be (ply, tick, capture and all); the shot is taken from where it lands.
   */
  | { type: "lance"; unicorn: number; at: number; step?: number; move?: number }
  /**
   * A Paladin turns up to `RULES.rallyPieces` of its own fodder, for its move.
   *
   * Each gets its own facing — they stand in different places, so one facing
   * for all is the Marshal's formation and not this. Reach is `knightReaches`,
   * the `REAIM_RADIUS` a knight's `re-aim` uses (§31 P1).
   */
  | { type: "rally"; paladin: number; turns: readonly { square: number; facing: Direction }[] }
  /**
   * Point a piece's hand-off at a neighbour, or clear it with `null`.
   *
   * Free of tempo, like re-pointing a belt: it is a routing setting, not an
   * action — and turn-bound like one too (§29 U1). Automatic routing stays the
   * default and is right nearly
   * always — this is for when the player can see something the gradient
   * cannot.
   */
  | { type: "set-handoff"; square: number; facing: Direction | null; owner: Color }
  /** Keep what you are holding, or go back to passing it along. */
  | { type: "set-dump"; square: number; dump: boolean; owner: Color }
  /**
   * Play a card from this turn's hand.
   *
   * Turn-bound, run-only, and the instance must actually be in hand — three
   * refusals, all in the reducer, because "the server rejects it" is not
   * available to a local game and should not be the only guard anywhere.
   */
  | {
      type: "play-card";
      instance: number;
      target?: number;
      /** The pieces a card names, and a square — the Static Moat's wall (§26 C13). */
      pieces?: number[];
      destination?: number;
      /**
       * The second square a two-square card needs — the Hydraulic Clasp's
       * other end, or the army the IFF Spoofer is copying.
       *
       * A second square, because those cards are about a *relationship* — this
       * piece, that piece. `CardPlay` has always declared it and the command
       * never carried it, so `refuse` saw `source: undefined` on every play and
       * answered "needs-a-target" forever: the card could not be played by
       * anybody, through any path. Invisible because the tests set the state
       * directly and asserted about what it *did*, so the play path had no
       * coverage at all — which is why `cards.test.ts` now drives it.
       */
      source?: number;
      /** The Spatial Manifold's edge (§31 C7). */
      direction?: Direction;
    }
  /**
   * Put a card in your deck out of nothing. **A development tool.**
   *
   * It is a command rather than a hand-written poke at the state because the
   * log is the save format, the replay and the network protocol — a cheat that
   * bypassed the reducer would produce a session that cannot be replayed, and
   * a bug found while using it could not be handed to anyone. Logged, it
   * reproduces exactly, and the log says plainly that a card was conjured.
   *
   * The server refuses it, like every other command that creates something.
   */
  | { type: "grant-card"; card: CardId }
  /**
   * Spend a card without playing it. **The Time Fluxxer's second half.**
   *
   * A rewind truncates the log, and the command that played the card is one of
   * the things it cuts — so a plain truncate hands the card back and the rewind
   * is free for ever. This is what the log ends with instead: the card was
   * spent, at a moment that no longer exists, and "spent" is the half that is
   * still true.
   *
   * **Not `play-card`, because "your hand" is a different question two turns
   * ago.** `dealHand` samples the deck once it passes `RULES.handSize`, so a
   * card in hand now need not have been in the hand at the point the rewind
   * lands, and `play-card` checks the hand — correctly, for a card being
   * played. What survives a rewind is ownership, not the deal, so this asks
   * only that the card is in the deck.
   *
   * Free and not turn-bound: the turn it belongs to is the one being handed
   * back, and charging for it would take the turn the rewind exists to return.
   */
  | { type: "burn-card"; instance: number }
  /**
   * Exchange a trophy for one of its three cards (§31 A2): into the deck and
   * the hand. `choice` indexes `Trophy.offer`. Turn-bound, run-only, free.
   */
  | { type: "claim-trophy"; trophyId: number; choice: number }
  /** Keep a trophy for score (§31 A2): `tally.trophies`. Turn-bound, run-only, free. */
  | { type: "keep-trophy"; trophyId: number }
  /**
   * Hand one piece of equipment (§31 A0) from a Monarch to a friendly Monarch
   * beside it. `item` indexes the giver's gear in list order (`gearBorneAt`).
   * Free, turn-bound, run-only: no networked game has an artifact.
   */
  | { type: "give-equipment"; owner: Side; from: number; to: number; item: number }
  /**
   * The Oil-Stone Scepter (§31 A4): `owner` plays `target`'s next turn — legal
   * moves only, End Turn included. Once every `RULES.seizeEvery` rounds while
   * a Sacred Well and its Scepter stand. Free, turn-bound, run-only.
   */
  | { type: "seize"; owner: Side; target: Side }
  /**
   * Switch a power on or off. **A development tool**, like `grant-card`, and a
   * command for the same reason: the log is the save format and the replay, so
   * a cheat that bypassed the reducer would produce a session nobody can
   * replay and a bug nobody can hand over.
   */
  | { type: "set-power"; power: PowerId; on: boolean }
  /**
   * Take one of your own pieces apart beside your king (Sacrifice, §12.3.6).
   *
   * **Half, at the grade it was made of.** A mint costs twice a piece's value
   * in material of its recipe's grade, so half a mint is exactly
   * `PIECE_VALUE[type]` units at `MINT_GRADE[type]` — a queen is 9 Diamond, a
   * rook 5 Gold, a bishop or knight 3 Silver, a pawn 1 Iron. There is no
   * rounding to argue about because the two numbers are the same number.
   *
   * That is also what separates a sacrifice from a death, and it is the whole
   * reason the power is worth an artifact: a captured piece leaves grade-0
   * scrap because it was blown apart, and a sacrificed one keeps its metal
   * because you took it apart on purpose. Without the difference this is "let
   * it be captured, but on your own turn".
   */
  | { type: "sacrifice"; square: number }
  /**
   * Buy a piece a `PIECE_VARIANTS` row — a sidegrade, not a rung: the piece
   * keeps its rank. Spends the turn, like a forge. The rank ladder was the
   * Clone Army's and went with it (§31 R2); `target` is the piece's own rank.
   */
  | { type: "upgrade"; square: number; target: PieceType; variant: number }
  /**
   * Fuse the pieces on `parts` into fuse row `into` (§31 P4): the row's first
   * part stays as the fused piece, the rest are consumed. Free like
   * `upgrade`, turn-bound, run-only (the Fusion Chamber's Signet). See
   * `campaign/fusion.ts`.
   */
  | { type: "fuse"; parts: readonly number[]; into: number }
  /**
   * Spend one Free Space tile, paving a square that is not on the board.
   *
   * Its own command rather than a second `play-card`, because the card and the
   * placements are different acts at different times: the card banks five and
   * is spent; this is one of the five, played whenever.
   */
  // A whole chain, never one square: "both ends touch ground" is a property of
  // the chain, and asked per square the pre-bridge board is already gone (§26 C9).
  | { type: "pave"; squares: number[] }
  /**
   * Open a region the level already described.
   *
   * Names a region rather than carrying one, so the reducer opens exactly what
   * the level said and a malformed command cannot conjure ground or an army.
   * Gated on the objective that reveals it being complete — the same
   * discipline as `next-stage`, which is gated on a side having no move.
   */
  | {
      type: "reveal-region";
      region: string;
      defeated?: Side;
      /**
       * A field the opening brings with it, and the ground it needed (§26 F3).
       *
       * **Mirrors `next-stage`'s pair rather than inventing a shape**: cells
       * open through the same `setCells` every other expansion uses, and
       * `placeDeposits` is still the one writer. A region describes ground a
       * *level* drew; this is the lobe a field grew for itself, so it is on the
       * command rather than in the level — a run's fields are generated, and a
       * replayed reveal has to put them back exactly where they were.
       *
       * Single-player like the command that carries them: `reveal-region` is
       * already in `SOLO_COMMANDS`.
       */
      deposits?: readonly DepositRow[];
      cells?: readonly number[];
    }
  /**
   * Open ground, and optionally stand something on it. **A development tool.**
   *
   * `reveal-region` opens a region a *level* already described and `next-stage`
   * is gated on a side having no move, so neither can answer "grow the board
   * now so I can look at something". This can, which is exactly why the server
   * refuses it alongside every other single-player command: it conjures ground,
   * an army and material in one call.
   *
   * Cells only ever *add*. A square that stopped existing under a piece is a
   * much worse bug than a board bigger than it needs to be, and every rule that
   * sweeps `cellList` would have to learn about it.
   */
  | {
      type: "dev-grow";
      cells?: number[];
      /**
       * Name an edge and the engine adds its row itself: the row just beyond it.
       * A caller need not know how squares are packed. Given with `cells`,
       * the two are simply combined.
       */
      edge?: BoardEdge;
      pieces?: [square: number, piece: number][];
      heaps?: [square: number, amount: number][];
      /**
       * A building stood up on bare ground, by `StructureKind` and owning side:
       * `pieces`' twin, for looking at a board with buildings on it.
       */
      structures?: [square: number, kind: number, owner: number][];
      /**
       * How much health each piece it stands up has (`Position.vitality`).
       *
       * Absent leaves them ordinary, on `PIECE_HEALTH`. This is what makes a
       * **tough** army — the one §27 is prototyped with — and it has to be per
       * square rather than per rank, because `PIECE_HEALTH` is keyed by rank
       * and so cannot make one army's pawns hard to shift and leave everybody
       * else's alone.
       */
      tough?: number;
      /** Armor each piece it stands up wears (`Position.plating`), `tough`'s twin. Absent is none. */
      plated?: number;
    }
  /**
   * Take ground away now. **A development tool**, and `dev-grow`'s inverse for
   * squares.
   *
   * It is `removeSquare` (the Micro-Singularity Bomb's) and nothing else.
   * Refuses a square a king stands on. In
   * `SOLO_COMMANDS`, so the server refuses it outright.
   */
  | {
      type: "dev-collapse";
      cells?: number[];
      /**
       * Name an edge and the engine adds its row itself: the outermost row on it.
       * A caller need not know how squares are packed. Given with `cells`,
       * the two are simply combined.
       */
      edge?: BoardEdge;
    }
  /**
   * Beat an army outright. **A development tool**, and `dev-grow`'s inverse.
   *
   * The two ways an army actually dies are both expensive to reach on purpose:
   * `next-stage` wants a side with no legal move, and `surrender` wants three
   * turns of a despair stamp. Neither answers "clear this camp so I can look at
   * what happens next", which is the question a smoke and a person debugging a
   * reveal both have — and the answer they reached for instead was worse. The
   * run smoke was deleting out of `position.board` by hand and then patching
   * `facing`, `counts` and `kings` to match: a fixture keeping four invariants
   * the reducer owns, past `applyCommand`, which every rule in this project
   * says is the one road in.
   *
   * It is `surrenderArmy` and nothing else, so a camp beaten this way leaves
   * exactly what a camp beaten properly leaves — scrap where it stood, cargo
   * spilled, a crown off the king, a roll off the loot table. A backdoor that
   * produced a *different* board would be a test passing against a game nobody
   * plays.
   */
  | { type: "dev-defeat"; side: Side }
  /**
   * Put material into a piece's hands, or take it back out.
   *
   * **The dev tool for the half of the game that takes longest to reach.** A
   * board with a stocked foundry, a full oven or a warehouse worth emptying is
   * twenty turns of honest mining away, so anything downstream of it — a
   * recipe, a tier, a store's taps, a turret climbing its ladder — is
   * expensive to look at even once.
   *
   * A **command** rather than a panel that writes to `industry`, for the
   * reason `dev-grow` and `dev-defeat` are commands: the log is the save
   * format, undo, replay and the network protocol, so a hand-written buffer is
   * a board that cannot be replayed and a hash that disagrees on the next
   * sync. In `SOLO_COMMANDS`, so the server refuses it outright.
   *
   * `delta` is signed and the square may hold a piece *or* a building —
   * `deliverUnit` already knows which, and taking material back out is the
   * same question asked of `takeUnit` and a store's stock.
   */
  | {
      type: "dev-material";
      square: number;
      grade: Grade;
      delta: number;
      /**
       * Which line the material is on (§26 F2). **Absent is metal**, so every
       * log written before lines existed replays identically; a rung no
       * ladder has is refused, never defaulted.
       */
      line?: MaterialLine;
    }
  /**
   * End your turn: give up every move left in it and let the machines run.
   *
   * A first-class action for any side, on its own turn, in a run and in
   * versus alike. It forfeits the *whole* turn however many moves are left —
   * one body, `forfeitTurn` — and hands the clock on through `endTurn`, so the
   * factories tick exactly as they would on a played turn. It is free: a turn
   * is what it costs, and there is nothing else to charge.
   *
   * **Refused while in check**, for re-aim's reason: it moves nothing, so it
   * cannot answer a check, and a side allowed to sit in one is a side check
   * never becomes mate for. Nothing else gates it — a stalemated side is
   * already refused by `requireOwnTurn`, since the status is terminal.
   *
   * Named `pass` for good: the log is the save format and old room logs carry
   * it, so a rename is a replay that breaks.
   *
   * **The planner never chooses it**, deliberately: the rules are symmetric
   * and which candidates a search considers is the planner's business, not
   * theirs.
   */
  | { type: "pass" }
  /**
   * Record that a side has been found **depressed**, or that it no longer is.
   *
   * Single-player only, and the server refuses it: the verdict is produced by
   * `src/engine/ai/depression.ts` — "could this army win if its opponent stopped
   * moving" is a question about how well the game can be played, which the
   * rules core has no way to ask and no business asking. So the AI decides and
   * the answer crosses as an ordinary command, exactly as every other thing the
   * opponent knows does.
   *
   * Idempotent on purpose. Re-stamping a side that is already depressed must
   * not restart its three turns, or an army would be reprieved by the check
   * that condemned it; and clearing one that is not depressed is a no-op.
   */
  | {
      type: "set-despair";
      side: Side;
      depressed: boolean;
      /**
       * Exempt this army for good, because it *arrived* unable to win.
       *
       * Sticky once set. A camp that lands as three pawns and a king is a
       * short fight, not a coma, and it was surrendering three turns after it
       * spawned — so the mechanic is switched off for an army that was never
       * going to be able to win rather than one that stopped being able to.
       */
      immune?: boolean;
    }
  /**
   * A depressed army lays down its arms.
   *
   * Gated in the reducer on the stamp being at least `RULES.despairTurns` old,
   * for the reason `next-stage` is gated on a side having no move: this
   * converts a whole army into scrap, and the server refusing it should not be
   * the only thing keeping it honest.
   */
  | { type: "surrender"; side: Side }
  /**
   * Post a king to one of its three jobs, or `null` to let it pick.
   *
   * Free of tempo, like the other machine settings: telling a king what to be
   * doing is not itself an action, and making it cost the turn would mean a
   * king could never be redirected in the turn you noticed you needed it
   * somewhere else. It is turn-bound all the same (§29 U1) — free, but on
   * your own clock.
   */
  | { type: "set-king-task"; square: number; task: number | null; owner: Color }
  /**
   * Turn your Kinetic or Cannon Turret on `square` to face `facing` (§31 S8:
   * "can rotate with a move"). **Spends a move**; turn-bound, owner-bound,
   * refused in check (it moves nothing). Replaces `set-turret-upgrade`,
   * retired with the tier ladder.
   */
  | { type: "aim-turret"; square: number; facing: Direction; owner: Color }
  /**
   * How much a piece holds back from the automatic passes.
   *
   * Machine policy like `set-dump`: **free of tempo**, turn-bound (§29 U1),
   * owner-bound to the seat. The number that lets a piece sit in a working
   * lane and still keep a stock the carry gradient would otherwise drain.
   */
  | { type: "set-reserve"; square: number; units: number; owner: Color }
;



/**
 * Stand extra armies on the board, at the opening.
 *
 * The one place a game can begin with more than the two sides a FEN can
 * describe. Everything the rules need for a third and fourth army already
 * exists — `growSides` files their kings, gives each its own alliance and its
 * own `counts` — so this is the placement and nothing else.
 *
 * **Every army opened with is a factory.** `growSides` hands a *plain* king to
 * a side that arrives mid-game, which is right for a wave: it did not build
 * what it is walking into. A side that was there at the opening built nothing
 * either, and neither did White — the rule is about arriving late, not about
 * being third. A skirmish where two of the four armies could not refine would
 * be a different game for each seat.
 */
function placeArmies(
  position: Position,
  armies: readonly [number, Side, PieceType, Direction][]
): void {
  if (armies.length === 0) return;

  let widest = position.sides;
  for (const [, side] of armies) widest = Math.max(widest, side + 1);
  growSides(position, widest);

  for (const [square, side, type, facing] of armies) {
    setPiece(position, square, makePiece(side, type));
    setFacing(position, square, facing);
    if (type === KING) position.kings[side] = square;
    setKingKind(position, side, KING_FACTORY);
  }
}

export function newGame(
  fen = START_FEN,
  seed = 0,
  // Readonly pairs, so a map's own table can be passed straight in without
  // being copied to make its tuples mutable.
  heaps: readonly (readonly [number, number])[] = [],
  movesPerTurn = 1,
  /** Card ids to open a run with, or undefined for an ordinary game. */
  run?: readonly CardId[],
  /** An authored level, whose start regions are placed immediately. */
  level?: LevelSetup,
  /** Set-piece dressing: lanes already laid and material already in hand. */
  belts: readonly [number, Direction, Color][] = [],
  stock: readonly (readonly [number, number, number, number?])[] = [],
  /** Armies past the two a FEN can name. See the `new-game` command. */
  armies: readonly [number, Side, PieceType, Direction][] = [],
  /** What kind of king each side plays. See the `new-game` command. */
  kingKinds: readonly number[] = [],
  /** A flow map per side, in `DEMAND_SLOTS` order. See the `new-game` command. */
  demand: readonly (readonly number[] | undefined)[] = [],
  /** Ground past the classic sixty-four. See the `new-game` command. */
  cells: readonly number[] = [],
  /** Which alliance each side is in. See the `new-game` command. */
  alliances: readonly number[] = [],
  /** Squares worth going to. See the `new-game` command. */
  objectives: readonly number[] = [],
  /**
   * Refined material lying on the floor, as square / grade / count.
   *
   * **Scrap is grade-0 by definition, so `heaps` cannot express this** — a heap
   * is what a corpse leaves. The mechanism already exists for levels
   * (`RegionSetup.metal`, applied in `openRegion`); this is the same thing
   * through the opening command, so a board can *open* on refined metal rather
   * than only reveal it mid-game.
   *
   * It goes to `industry.dropped`, the ground: anyone walking over it picks it
   * up, `vacuumAlong` lifts it exactly as it lifts scrap, and a `collect`
   * objective counts it without being taught anything.
   *
   * **Single-player only, refused by the server beside `heaps`** — between them
   * they conjure a stockpile nobody built.
   */
  metal: readonly [number, number, number][] = [],
  /**
   * Hard Mode, for a run or a level (§26 F13). See `campaign/mode.ts`.
   *
   * Ignored without one, because there is no run for it to be the constitution
   * of, and stored only when `"hard"` — the field's own rule.
   */
  mode: RunMode = "normal",
  /** Coal on the floor, as square / count (§31 M2). See the `new-game` command. */
  coal: readonly (readonly [number, number])[] = []
): GameState {
  const position = parseFen(fen);
  // **Before the armies, because a piece needs somewhere to stand.** The board
  // a scenario opens on is the FEN's sixty-four *plus* whatever ground it
  // says runs past them, and a padded board puts its outer armies out there.
  if (cells.length > 0) setCells(position, [...position.cells, ...cells]);
  placeArmies(position, armies);

  // A level supplies the whole board, so it starts from nothing.
  //
  // Without this the classic sixty-four squares and thirty-two pieces are
  // underneath every authored map: a region's king lands on a square a rook
  // already holds and is silently dropped, and a level's heap sits on ground
  // it never asked for. A level is not a variant of chess's opening position;
  // it is a different board.
  if (level) {
    setCells(position, []);
    position.board.clear();
    position.facing.clear();
    position.kings.fill(-1);
    position.counts.fill(0);
    position.castling = 0;
    position.ep = -1;
    position.turn = level.playerSide;
  }
  // Clamped rather than trusted: this comes off the wire, and a zero would
  // hand the clock to nobody while a huge one would hand it to one side for
  // the rest of the game.
  position.movesPerTurn = Math.max(1, Math.min(RULES.maxMovesPerTurn, Math.floor(movesPerTurn)));
  position.movesLeft = position.movesPerTurn;
  position.fodderLeft = position.movesPerTurn * RULES.armyLogistics;
  const industry = createIndustry();
  seedIndustry(industry);
  for (const [square, amount] of heaps) setHeap(industry, square, amount);
  // A set piece may open with a factory already standing. Nothing else may:
  // the server refuses these alongside `heaps`, because between them they
  // conjure a network and a stockpile nobody built.
  for (const [square, direction, owner] of belts) setBelt(industry, square, direction, owner);
  // Refined material on the floor rather than in anybody's hands — the same
  // `dropped` map `openRegion` writes a level's `metal` into, and the same
  // guard: a square the board does not have is skipped rather than creating an
  // orphan entry nothing can ever reach.
  for (const [square, grade, count] of metal) {
    if (!hasCell(position, square)) continue;
    for (let i = 0; i < count; i += 1) {
      dropOnGround(industry, square, makeUnit(grade as 0 | 1 | 2 | 3));
    }
  }
  // Coal the same way, on the fuel line (§31 M2).
  for (const [square, count] of coal) {
    if (!hasCell(position, square)) continue;
    for (let i = 0; i < count; i += 1) dropOnGround(industry, square, makeUnit(COAL, 0, FUEL));
  }

  const state: GameState = {
    position,
    industry,
    history: [],
    repetitions: new Map([[repetitionKey(position), 1]]),
    status: "playing",
    seed
  };
  if (run || level) {
    // **A card the table has never heard of is dropped, not dealt.**
    // Everything downstream looks the id up in `CARD_TABLE` and reads the
    // entry, so an unknown one is `undefined.name` — a white screen rather
    // than a missing card, and the whole run is unplayable. That is not
    // hypothetical: retiring Load Em Up took its id out of `CARDS`, and every
    // saved run and every fixture that named it crashed on load.
    //
    // Dropped rather than refused, because the alternative to a run with one
    // fewer card is no run at all.
    const dealt = (run ?? []).filter((card) => CARD_TABLE[card] !== undefined);
    // The player is White, named rather than assumed. The hand is dealt now
    // rather than on the first `endTurn`, or the opening turn of a run is the
    // one turn you have no cards.
    const opened: RunState = {
      side: level?.playerSide ?? WHITE,
      deck: dealt.map((card, id) => ({ id, card })),
      hand: [],
      nextCardId: dealt.length,
      spent: [],
      level,
      revealed: [],
      objectives: level ? openObjectives(level) : []
    };
    // **Stored only off the default**, so a run that spelled "normal" out is
    // indistinguishable from one that said nothing — which is what keeps "with
    // and without the field" hashing identically, `ruleset`'s own rule.
    if (mode === "hard") opened.mode = "hard";
    opened.hand = dealHand(opened, seed, 0);
    state.run = opened;
    // **Pawns by mode** (§31 P2): chess's pawn rules are a fixed PvP map's;
    // a run has none of them. On the position, because movegen sees no more.
    position.runPawns = true;

    // Everything the level says starts on the board. The rest waits for its
    // objective, and `reveal-region` opens it by name.
    level?.regions.forEach((region, index) => {
      if (region.revealedBy !== "") return;
      // Its zones first (§31 D3), so the room's own tiles and pieces land on
      // the ground they grew.
      growRegionZones(position, state.industry, region, seed, index);
      openRegion(state, region);
      opened.revealed.push(region.id);
    });
  }

  // The fourth field names the line (absent is metal, so old logs replay):
  // a set piece can open with cobble in hand for its track (§31 S2). After
  // the level, so a square a level builds on is there to take it: **a
  // structure on the square takes it into its stock, offering what it was
  // given** (Training's store, §33 I1 — a sealed stockpile is no stockpile),
  // anything else into the piece's hands.
  for (const [square, grade, count, line] of stock) {
    const store = structureAt(industry, square);
    if (!store) {
      addToBuffer(industry, square, grade as 0 | 1 | 2 | 3, count, "in", (line ?? METAL) as MaterialLine);
      continue;
    }
    for (let i = 0; i < count; i++) claimStock(store, makeUnit(grade, 0, (line ?? METAL) as MaterialLine));
    store.offer = withGrade(store.offer, grade, true);
  }

  // Last, because a level and `placeArmies` both hand out king kinds of their
  // own and this is the declaration that wins — it is the only one a player
  // made on purpose.
  kingKinds.forEach((kind, side) => {
    if (!FACTIONS.includes(kind)) throw new IllegalCommandError(`unknown faction: ${kind}`);
    if (side < position.sides) setKingKind(position, side, kind);
  });

  // And each army's flow map, on the same footing and for the same reason.
  demand.forEach((tiers, side) => {
    if (tiers && side < position.sides) setDemand(position, side, tiers);
  });

  // Somewhere worth going. Only the squares that exist — an objective off the
  // board is one no piece can ever reach, and a planner pulled toward it would
  // walk into the edge forever.
  for (const sq of objectives) {
    if (hasCell(position, sq)) position.objectives.add(sq);
  }

  // Alliances last too, and after `placeArmies` for the reason it is written
  // in sides rather than in squares: `growSides` is what makes side three
  // exist, and it puts every new army in an alliance of its own.
  alliances.forEach((alliance, side) => {
    if (side < position.sides) position.alliances[side] = alliance;
  });

  // Named before anybody can ask; `applyCommand` keeps it so from here.
  stampPieceIds(position);
  // And keyed after it: naming arms a body (§31 K1), and armor is in the key.
  state.repetitions = new Map([[repetitionKey(position), 1]]);

  state.status = gameStatus(
    position,
    legalMoves(position).length,
    state.repetitions,
    !state.run,
    kinglessGrace(state.run)
  );
  return state;
}

/**
 * While a Confessor's grant is out, the side's move is the empowered piece's.
 *
 * **The one enforcement point, and it is the reducer rather than movegen** —
 * see `Position.empowered`. Two ways the grant lets go rather than trapping a
 * player, both checked here so no other path has to remember them:
 *
 *  - **The piece is gone.** Nothing here moved it, but a bash shoves pieces, so the square can hold somebody else's
 *    piece or nobody by the time the player clicks.
 *  - **The piece has nothing to play.** A grant that forbade every other move
 *    while its own holder was blocked would be a turn the player cannot spend
 *    — a soft-lock built out of a prize. `legalMoves` is memoised on the
 *    position, so this is a filter over a list the caller has already built.
 */
function refuseUnempowered(position: Position, from: number): void {
  const grant = position.empowered;
  if (!grant || grant.square === from) return;
  const piece = pieceAt(position, grant.square);
  if (!piece || pieceColor(piece) !== grant.side) return;
  if (!legalMoves(position).some((move) => moveFrom(move) === grant.square)) return;
  throw new IllegalCommandError("the empowered piece has moves to spend first");
}

function applyMove(state: GameState, command: Extract<Command, { type: "move" }>): GameState {
  // The turn's gates first (§31 T2, T4): no moves left, or a forge already
  // made — and this piece's own (§33 T1): its pool, and once a turn. An
  // Empower network's extra move has its own gate instead (§33 S1).
  if (command.boost === undefined) requireMove(state, command.from);
  else {
    const why = boostRefusal(state.position, state.industry, state.position.turn, command.from, command.boost);
    if (why) throw new IllegalCommandError(why);
  }
  const move = findLegalMove(state.position, command.from, command.to, command.promotion);
  if (move === undefined) {
    throw new IllegalCommandError(
      `illegal move ${command.from}->${command.to} in ${state.status}`
    );
  }
  refuseUnempowered(state.position, command.from);
  const next = cloneState(state);
  const { position, industry } = next;
  const mover = position.turn;
  const from = moveFrom(move);
  const to = moveTo(move);
  const wasIrreversible =
    pieceType(pieceAt(position, from)) === PAWN ||
    // A blow that did not kill counts too. Provable until a Miracle Healer put
    // health back; conservative now — the reset only delays a fifty-move draw,
    // and recurrence is decided by `hurt` being in `repetitionKey`.
    isBash(move) ||
    (!isBash(move) && pieceAt(position, to) !== 0) ||
    // A structure capture is as irreversible as any other: the building is
    // gone for good, so no earlier position can recur (§26 F5) — and so is
    // a plow through one (§31 S1).
    isStructureCapture(move) ||
    move.broken.length > 0;

  // The scorecard the round card between stages reports (`tally.ts`).
  //
  // Read off the *move record* rather than off the board, so an en passant
  // counts like any other capture — the victim is not on `to`, and the one
  // place that difference has already cost a bug is four lines below this.
  // A player cannot capture their own piece, so the mover being the counted
  // side is the whole of the ownership test.
  if (mover === tallySide(state.run)) {
    const victim = moveCapturedType(move);
    next.tally = addTally(next.tally, {
      moves: 1,
      taken: victim === 0 ? 0 : 1,
      // Priced by `SURRENDER_VALUE`, the one table for what a piece was worth
      // when it died — `PIECE_VALUE` puts a king at nought, and taking an
      // exposed king is the capture that ends a front.
      score: victim === 0 ? 0 : SURRENDER_VALUE[victim],
      ringOuts: isRingOut(move) ? 1 : 0
    });
  }

  // Everything a captured piece was carrying drops where it died, as raw
  // scrap — grade does not survive its carrier.
  // **A victim beside the capturer's altar is consumed whole** (§26 A1):
  // its full mint bill, refined, into the altar's stock — no heap, no corpse
  // mark, no deposit burial. Asked of the *victim's* square, which for an en
  // passant is not the square the capturer lands on. Pieces only — a captured
  // structure keeps F5's wreck, since a warehouse has no mint bill.
  // **Or beside a Kris bearer, or taken by one** (§31 A3): the bearer's
  // inventory — its square before the move, so `migratePiece` carries it on.
  // Either needs the Sacrifice power: altar and Kris both standing (§31 A0).
  const spillOrClaim = (victim: number): void => {
    const piece = pieceAt(position, victim);
    const furnace = furnaceBeside(industry, mover, victim, from);
    if (furnace?.kind === "altar") {
      claimPieceAtAltar(industry, victim, piece, furnace.at);
    } else if (furnace?.kind === "kris") {
      claimPieceToBearer(industry, victim, piece, furnace.at);
    } else {
      spillPiece(industry, victim, piece);
    }
  };
  // Nuclear Generators this move destroys (§31 S6), detonated after it.
  const blasts: number[] = [];
  if (isEnPassant(move)) {
    // One rank behind the square the capturer landed on. `16` was the 0x88
    // stride and survived the move to packed coordinates, where a rank is
    // STRIDE — so this addressed a square sixteen files away, found nothing
    // there, and quietly spilled nothing. Every other capture left a heap and
    // this one did not, which made an en passant the one way to remove a piece
    // from the game without its material entering the economy.
    spillOrClaim(to + (mover === WHITE ? -STRIDE : STRIDE));
  } else if (isStructureCapture(move)) {
    // The industry's half of a structure capture (§26 F5): the building is
    // wrecked where it stood — stock as scrap, fabric as cobble — before
    // `makeMove` stands the capturer on the square. No loot roll: loot comes
    // off kings, and a warehouse's prize is the spill itself.
    // A Nuclear Generator goes off once the capturer stands there (§31 S6).
    if (isNuclearAt(industry, to)) blasts.push(to);
    wreckStructure(position, industry, to);
  } else if (isStructureBlow(move)) {
    // A blow that did not finish it (§27): the wall still stands and takes the
    // damage. The industry's half alone — `makeMove` writes the mirror — and
    // read off `from` before anything moves, since `blowOf` asks what is
    // standing there. `moveTarget` is the building's square; `to` is where the
    // attacker stopped, which is a different square whenever it slid in.
    hurtStructure(industry, moveTarget(move), blowOf(position, from) - move.spent);
  } else if (!isBash(move) && pieceAt(position, to) !== 0) {
    // **Not a bash** (§27): the piece on `to` is still standing — it took the
    // blow and lost health, or it was driven on and something else is there.
    // Spilling here would leave a corpse for a piece still on the board, and
    // roll it loot.
    spillOrClaim(to);
  }

  // **The plow's wrecks** (§31 S1): every non-obstruction structure the move
  // destroys — crossed, crushed on landing, or broken by a shoved piece —
  // wrecked by F5's rule, stock as scrap and fabric as cobble. The industry's
  // half; `makeMove` clears the mirror, so a search never wrecks a real one.
  // A belt part (§31 S2) wrecks as a part: cargo as scrap, fabric as cobble.
  for (const sq of move.broken) {
    if (hasBelt(industry, sq)) wreckBelt(position, industry, sq);
    else wreckStructure(position, industry, sq);
  }

  // Read before `makeMove` walks the costume across; no clone since §26 C12.
  const worn = disguisedAs(position, from);
  let unmasked: { square: number; reason: DisguiseBreak } | null = null;

  // **The hold** (§31 T2): headroom, so `passTurn` inside `makeMove`
  // decrements instead of flipping the side — the core flips exactly as perft
  // expects, and the clock stays here until End Turn. A fixed two and put
  // back after, since a Fodder Move may come with no Power Move left (§33
  // T1); `spendMove` then takes the move off the right pool.
  const held = position.movesLeft;
  position.movesLeft = 2;
  makeMove(position, move);
  position.movesLeft = held;

  // Buffers, ovens, targets and facing belong to the piece, so they travel.
  // **Guarded on the squares differing**, because a bash whose shove was
  // refused can leave the attacker where it started — a knight has no approach
  // to stop on — and `migratePiece` writes the destination and then clears the
  // source, which for one square is the piece's own holdings deleted.
  if (from !== to) migratePiece(industry, from, to);

  // A promotion is a new rank on the same body: armor survives it, and the
  // base moves by the ranks' difference (§31 K1) — a pawn queened gains none,
  // a pawn rooked gains the rook's ten.
  const promoted = movePromotion(move);
  if (promoted) reframePlating(position, to, PIECE_PLATING[promoted] - PIECE_PLATING[PAWN]);

  // **And the piece that was shoved takes its factory with it** (§27). It did
  // not move itself, but it moved: its buffers, its oven and what it was
  // saving for are all still its own, and a piece that arrived empty because
  // somebody hit it would be a way to destroy an economy by touching it.
  // The shove landed exactly when the attacker took the target's square, and
  // `makeMove` has already driven it on — so its new square is one step
  // further along the same blow. `bounceSquare` never answers the target's
  // square, so the two cases cannot be confused.
  const drivenTo =
    isBash(move) && move.shoved === to ? to + blowStep(from, move.shoved) : -1;
  if (drivenTo >= 0) {
    migratePiece(industry, move.shoved, drivenTo);
    // Everything a piece arriving on a square does, exactly as the mover does
    // it: it lifts what is lying there. A part it lands on is already in
    // `move.broken` — a shove breaks any (§31 S2).
    pickUpDropped(position, industry, drivenTo);
  }

  // **And the clamped piece takes its factory with it** (§26 C11), the shoved
  // one's reason exactly. Here rather than in `makeMove` because `legalMoves`
  // makes and unmakes every candidate, so a search would migrate a real hopper
  // by thinking about a move. After the mover's own migrate, or a drag onto the
  // square it vacated is clobbered.
  if (isDrag(move)) {
    const draggedTo = dragSquare(from, to, move.dragged);
    migratePiece(industry, move.dragged, draggedTo);
    crushBeltUnder(position, industry, draggedTo, pieceColor(pieceAt(position, draggedTo)));
    pickUpDropped(position, industry, draggedTo);
  }

  // The cover, tested by the move just made — here and never in `makeMove`,
  // which movegen would run on every candidate. `to`, not `from`.
  if (worn) {
    const broke = disguiseBroken(position, move, worn);
    if (broke) {
      setDisguise(position, to, null);
      unmasked = { square: to, reason: broke };
    }
  }

  // A piece that ends its move on a dropped artifact picks it up — one per
  // move, into its buffer.
  //
  // Not a mine: mining is rate-limited and clamps to the heap, and an artifact
  // is not scrap. Walking a piece over to fetch one is the manual route and
  // the belt is the convenient one; without this the only way to collect a
  // crown would be to build a lane to wherever the enemy king happened to die.
  pickUpDropped(position, industry, to);
  if (isCastle(move)) {
    const rank = rankOf(to);
    const kingSide = fileOf(to) === 6;
    migratePiece(
      industry,
      square(kingSide ? 7 : 0, rank),
      square(kingSide ? 5 : 3, rank)
    );
  }

  // A promoted pawn becomes a piece that cannot hold ore, so it drops what it
  // was carrying rather than stranding it.
  if (movePromotion(move)) {
    const held = stripHoldings(industry, to);
    if (held > 0) addHeap(industry, to, held);
  }

  // An enemy part under `to` is in `move.broken` (§31 S2) and already wrecked.
  // **The queen and the king lift what they move over**, and only those two.
  //
  // The queen is a collector because her *path* is the long one — she is the
  // piece that can cross a graveyard in a move. The king is here for the
  // opposite reason: it moves one square, so its path is only the square it
  // arrives on, and what this buys is *instant pickup*. Before it, walking the
  // king onto a heap meant standing there while `mineOnce` drew the pile down
  // a dig at a time; now the metal is in its hands the moment it arrives, and
  // the digging rate is what governs a heap too big to carry rather than the
  // price of picking anything up at all.
  //
  // It is the same function on purpose. Everything the queen's sweep already
  // settles — a lane underfoot takes the haul first, a touching consumer takes
  // it next, an artifact never goes on a belt, and anything nothing will
  // accept goes back on the ground it came off — is exactly what a king
  // arriving on a pile needs answered, and a second implementation would drift
  // on all four.
  const arrived = pieceType(pieceAt(position, to));
  let swept = 0;
  if (arrived === QUEEN || arrived === KING) {
    swept = vacuumAlong(position, industry, from, to, mover);
    // **A sweep is work, and the scorecard has to say so.** `mined` was only
    // ever the dig pass, so a king — which cannot dig at all — collected
    // material and the run reported none. A `collect` objective reads this,
    // and `first-light`'s opening is exactly a king walking over scrap.
    if (swept > 0 && mover === tallySide(state.run)) {
      next.tally = addTally(next.tally, { mined: swept });
    }
  }

  // **Spent here, in the reducer, and never in `makeMove`.** The grant names a
  // square and the piece has just left it, so it has to follow — and doing that
  // where a search could reach it would need `unmakeMove` to put it back.
  // Asked of the square the move *started* on, because that is what the grant
  // was about; `to` is where the piece is now, bash or not.
  if (position.empowered?.square === from) spendEmpowered(position, to);

  // **The blast** (§31 S6): after the move, so the capturer is on the square.
  for (const at of blasts) detonate(position, industry, at);
  // **Overwatch** (§31 S8): enemy turrets with shots left fire at the piece
  // that entered their field — after the move, never in `makeMove`.
  const watched = overwatch(position, industry, from, to);

  next.history = [...state.history, move];
  if (command.boost !== undefined) {
    // Read off `state`: the piece has left `from` on `next`.
    drawNetworkFuel(industry, mover, command.boost, boostFuel(state.position, from));
    const source = structureAt(industry, command.boost);
    if (source) source.boosts = (source.boosts ?? 0) + 1;
  }
  spendMove(state, next, mover, wasIrreversible, true, from, command.boost === undefined ? false : "boost");
  // **Folded in after `spendMove`**, which writes `lastTick` wholesale — the
  // order the forge's own report is written in, and for the
  // same reason: setting it first is how a report goes missing while the thing
  // it describes happened. Only when something was actually lifted, so a board
  // with no sweep on it carries no field at all.
  if (swept > 0 && next.lastTick) {
    next.lastTick = { ...next.lastTick, collected: { at: to, amount: swept } };
  }
  // **Folded in after `endTurn`, because that is what builds the report.** The
  // unmasking is a fact about the *move* rather than about the tick, but
  // `lastTick` is the channel the renderer already reads for "what just
  // happened", and a second one would be a second thing to keep in step.
  if (unmasked && next.lastTick) next.lastTick = { ...next.lastTick, unmasked: [unmasked] };
  if (watched.length > 0 && next.lastTick) {
    next.lastTick = {
      ...next.lastTick,
      shots: [...watched, ...(next.lastTick.shots ?? [])],
      killed: (next.lastTick.killed ?? 0) + watched.filter((shot) => shot.killed).length
    };
  }
  return next;
}

/**
 * A knight spends its *whole move* to point a friendly pawn somewhere else.
 *
 * Any pawn of yours within `REAIM_RADIUS` (§31 P1); `step` also walks it one
 * non-capturing square forward on the new facing, in the same move. Illegal
 * while in check — re-aim's old reason, kept even for a step: a re-aim is
 * not a reply to check, so a side in check answers with a move. A step is a
 * piece moved without a move (lance's door): `requireKingSafe`, and it does
 * not promote — the pawn's own move does.
 */
function applyReAim(
  state: GameState,
  command: Extract<Command, { type: "re-aim" }>
): GameState {
  const { position } = state;
  const mover = position.turn;

  if (state.status !== "playing") {
    throw new IllegalCommandError(`cannot re-aim in ${state.status}`);
  }
  const knight = pieceAt(position, command.knight);
  if (!knight || pieceColor(knight) !== mover || pieceType(knight) !== KNIGHT) {
    throw new IllegalCommandError("not your knight");
  }

  const pawn = pieceAt(position, command.pawn);
  if (!pawn || pieceColor(pawn) !== mover || pieceType(pawn) !== PAWN) {
    throw new IllegalCommandError("not your pawn");
  }

  if (!knightReaches(command.knight, command.pawn)) {
    throw new IllegalCommandError(`that pawn is more than ${REAIM_RADIUS} squares from the knight`);
  }

  const to = command.step ? reAimStep(command.pawn, command.facing) : -1;
  if (command.step) {
    // The step is the pawn's move for the turn (§33.1 Q5), so a pawn that
    // has moved takes none.
    const pawnId = pieceIdAt(position, command.pawn);
    if (pawnId > 0 && position.turnMoved.has(pawnId)) {
      throw new IllegalCommandError("that pawn has already moved this turn");
    }
    if (armourLeft(position, command.pawn) > 0) {
      throw new IllegalCommandError("a frozen pawn cannot step");
    }
    if (to < 0 || !standable(position, to)) {
      throw new IllegalCommandError("the square in front of the pawn is blocked");
    }
    if (promotesOn(position, mover, to)) {
      throw new IllegalCommandError("a re-aim step cannot promote — move the pawn there");
    }
  }

  const next = cloneState(state);
  setFacing(next.position, command.pawn, command.facing);
  if (command.step) {
    // `shovePiece` carries the facing just set; then what any arriving piece does.
    shovePiece(next.position, command.pawn, to);
    migratePiece(next.industry, command.pawn, to);
    crushBeltUnder(next.position, next.industry, to, mover);
    pickUpDropped(next.position, next.industry, to);
    requireKingSafe(next.position, mover, "re-aim");
    // A pawn moved: irreversible, as a pawn push is.
    next.position.halfmove = 0;
  } else {
    next.position.halfmove++;
  }
  // **The knight forgoes its move; no pool is spent** (§33 T3). The step is
  // a bonus move, the pawn's for the turn (Q5): marked, never pooled.
  const why = freeMoveRefusal(state, command.knight);
  if (why) throw new IllegalCommandError(why);
  spendMove(state, next, mover, Boolean(command.step), false, command.knight, "knight");
  if (command.step) {
    const pawnId = pieceIdAt(next.position, to);
    if (pawnId > 0) next.position.turnMoved.add(pawnId);
  }
  return next;
}

/** The square one forward of `from` on `facing`, by coordinates (an offset wraps), or -1. */
function reAimStep(from: number, facing: Direction): number {
  const file = fileOf(from) + (facing === EAST ? 1 : facing === WEST ? -1 : 0);
  const rank = rankOf(from) + (facing === NORTH ? 1 : facing === SOUTH ? -1 : 0);
  if (file < COORD_MIN || file > COORD_MAX || rank < COORD_MIN || rank > COORD_MAX) return -1;
  return square(file, rank);
}

/**
 * A Confessor hands the piece beside her two moves of its own.
 *
 * **Allowed in check, unlike `drill`, and the difference is that this one can
 * answer one.** A drill moves nothing and would be a free pass; this spends her
 * whole turn and hands the moves to a piece that may block, take or run — so it
 * is an ordinary answer to a check and is priced like one. `requireKingSafe` is
 * not asked for the same reason: nothing moves here, so no line opens.
 *
 * **The grant is bounded by the piece and the turn.** Two moves, to that piece,
 * lapsing when the clock leaves — see `passTurn`, which is where a turn's
 * unspent tempo is thrown away already.
 */
function applyEmpower(
  state: GameState,
  command: Extract<Command, { type: "empower" }>
): GameState {
  const { position, industry } = state;
  const mover = requireOwnTurn(state);

  const piece = pieceAt(position, command.confessor);
  if (!piece || pieceColor(piece) !== mover) {
    throw new IllegalCommandError("not your piece");
  }
  if (!pieceVariant(variantAt(position, command.confessor)).empowers) {
    throw new IllegalCommandError("that piece empowers nobody");
  }
  if (command.target === command.confessor) {
    throw new IllegalCommandError("a Confessor cannot empower herself");
  }
  if (!touches(command.confessor, command.target)) {
    throw new IllegalCommandError("that piece is not beside the Confessor");
  }
  const target = pieceAt(position, command.target);
  if (!target || pieceColor(target) !== mover) {
    throw new IllegalCommandError("not your piece to empower");
  }

  // **Rounds off `round` (§31 T1)**, as every per-turn timer now is. A round
  // is `sides` plies only while a turn is one move — and this is the one
  // ability on the board that *hands out moves*, so counting plies would have
  // her grant tick down her own cooldown. It read `fullmove`,
  // which wraps on side 0 and so never advanced once White was gone.
  const last = preachedAtOn(industry, command.confessor);
  if (last >= 0 && position.round < last + RULES.empowerRounds) {
    throw new IllegalCommandError("she has already empowered somebody recently");
  }

  const next = cloneState(state);
  setPreachedAt(next.industry, command.confessor, position.round);
  // **Granted before the turn is spent**, so `passTurn` decrements what she
  // gave rather than what she had: one move in, `empowerMoves` out, and the
  // clock stays with the side because `movesLeft` is above one.
  next.position.movesLeft = Math.min(
    RULES.maxMovesPerTurn,
    next.position.movesLeft + RULES.empowerMoves
  );
  setEmpowered(next.position, command.target, mover, RULES.empowerMoves);

  next.position.halfmove++;
    spendMove(state, next, mover, false, false, command.confessor);
  return next;
}

/**
 * A Unicorn Knight shuffles a square and strikes at range, for its whole turn.
 *
 * **`requireKingSafe`, where `drill` needs none**: a step opens a line. The
 * step is the sixth door that moves a piece without a move (C13 counted five),
 * so it reuses the bash's bodies. `docs/claude/armies.md`.
 */
function applyLance(state: GameState, command: Extract<Command, { type: "lance" }>): GameState {
  const { position } = state;
  const mover = requireOwnTurn(state);

  const piece = pieceAt(position, command.unicorn);
  if (!piece || pieceColor(piece) !== mover) {
    throw new IllegalCommandError("not your piece");
  }
  if (!lancesFrom(position, command.unicorn)) {
    throw new IllegalCommandError("that piece carries no horn");
  }

  // **What it deals is the row's** (§31 P4: the Alicorn's 7), else the Unicorn's.
  const row = pieceVariant(variantAt(position, command.unicorn));
  const dealt = row.lanceDamage ?? RULES.lanceDamage;
  if (command.move !== undefined) return lanceOnMove(state, command, mover, dealt);

  // Reach is measured from where it lands, which is what the step buys.
  let from = command.unicorn;
  if (command.step !== undefined) {
    if (!lanceSteps(position, command.unicorn).includes(command.step)) {
      throw new IllegalCommandError("a lance steps one orthogonal square onto bare ground");
    }
    from = command.step;
  }

  requireLanceTarget(position, mover, from, command.at);

  const next = cloneState(state);
  if (command.step !== undefined) {
    shovePiece(next.position, command.unicorn, command.step);
    // Its factory travels, then what any arriving piece does — the bash's three.
    migratePiece(next.industry, command.unicorn, command.step);
    crushBeltUnder(next.position, next.industry, command.step, mover);
    pickUpDropped(next.position, next.industry, command.step);
  }
  damage(next.position, next.industry, command.at, dealt);

  requireKingSafe(next.position, mover, "lance");

  next.position.halfmove++;
    spendMove(state, next, mover, false, false, command.unicorn);
  return next;
}

/** Refuse a horn's target out loud, from `from`. */
function requireLanceTarget(position: Position, mover: Side, from: number, at: number): void {
  const target = pieceAt(position, at);
  if (!target) {
    throw new IllegalCommandError("nothing there to lance");
  }
  if (!areEnemies(position, mover, pieceColor(target))) {
    throw new IllegalCommandError("that one is not against you");
  }
  if (!withinHorn(from, at)) {
    throw new IllegalCommandError("out of the horn's reach");
  }
  // Ranged, so an obstruction between blocks it (§31 S1).
  if (!clearLine(position, from, at)) {
    throw new IllegalCommandError("a wall blocks the horn's line");
  }
  // Out loud: `damage` would refuse a last king and a frozen piece in silence.
  if (!canBeHurt(position, at, target)) {
    throw new IllegalCommandError("nothing the horn can hurt there");
  }
}

/**
 * The Alicorn's "jump and shoot in the same move" (§31 P4): one of
 * its legal moves, made exactly as `move` makes it (ply, tick, capture, the
 * blow's shove), then the horn from wherever the body stands — found by its
 * id, so a bash that leaves it home shoots from home. The move pays the turn.
 */
function lanceOnMove(
  state: GameState,
  command: Extract<Command, { type: "lance" }>,
  mover: Side,
  dealt: number
): GameState {
  const { position } = state;
  if (!pieceVariant(variantAt(position, command.unicorn)).lancesOnMove) {
    throw new IllegalCommandError("only a Alicorn shoots on its move");
  }
  if (command.step !== undefined) {
    throw new IllegalCommandError("a lance takes a step or a move, not both");
  }
  const id = pieceIdAt(position, command.unicorn);
  const next = applyMove(state, { type: "move", from: command.unicorn, to: command.move! });
  let from = command.move!;
  if (id > 0) for (const [sq, one] of next.position.pieceId) if (one === id) from = sq;
  requireLanceTarget(next.position, mover, from, command.at);
  damage(next.position, next.industry, command.at, dealt);
  requireKingSafe(next.position, mover, "lance");
  return next;
}

/**
 * A Paladin turns up to `RULES.rallyPieces` of its own fodder, for its move.
 *
 * **`drill`'s shape at `re-aim`'s reach**: validated whole before anything is
 * written, so an order naming one bad square turns nobody. No `requireKingSafe`
 * — a facing decides where *your* pieces go — and refused in check, because it
 * moves nothing and would otherwise be a free pass.
 */
function applyRally(state: GameState, command: Extract<Command, { type: "rally" }>): GameState {
  const { position } = state;
  const mover = position.turn;

  if (state.status !== "playing") {
    throw new IllegalCommandError(`cannot rally in ${state.status}`);
  }

  const piece = pieceAt(position, command.paladin);
  if (!piece || pieceColor(piece) !== mover) {
    throw new IllegalCommandError("not your piece");
  }
  if (!pieceVariant(variantAt(position, command.paladin)).rallies) {
    throw new IllegalCommandError("that piece gives no orders");
  }

  // Deduped by square, `drill`'s reason: this crosses the socket and lands in a
  // log replayed to every joiner. A `Map` keeps order, so refusals name the
  // first bad square — and two orders for one piece are one order, not two.
  const turns = new Map<number, Direction>();
  for (const turn of command.turns) turns.set(turn.square, turn.facing);
  if (turns.size > RULES.rallyPieces) {
    throw new IllegalCommandError(`a Paladin turns at most ${RULES.rallyPieces}`);
  }

  for (const square of turns.keys()) {
    if (square === command.paladin) {
      throw new IllegalCommandError("a Paladin does not rally itself");
    }
    // The same reach `re-aim` uses, so what a Paladin may turn and what a
    // knight may turn are one question with one answer.
    if (!knightReaches(command.paladin, square)) {
      throw new IllegalCommandError("that piece is out of the Paladin's reach");
    }
    const held = pieceAt(position, square);
    if (!held || pieceColor(held) !== mover) {
      throw new IllegalCommandError("not your piece to turn");
    }
    // The class table, asked of the *square* — a row may disagree with its rank.
    if (pieceClassAt(position, square) !== CLASS_FODDER) {
      throw new IllegalCommandError("only fodder takes a Paladin's order");
    }
  }

  const next = cloneState(state);
  for (const [square, facing] of turns) setFacing(next.position, square, facing);

  next.position.halfmove++;
    spendMove(state, next, mover, false, false, command.paladin);
  return next;
}

/**
 * A Marshal turns, and takes the fodder it is touching round with it.
 *
 * **No `requireKingSafe`, and that is a claim rather than an omission**: a
 * facing decides where *your* pieces go, so turning one opens no line on your
 * own king. It can give check, which is ordinary.
 */
function applyDrill(
  state: GameState,
  command: Extract<Command, { type: "drill" }>
): GameState {
  const { position } = state;
  const mover = position.turn;

  if (state.status !== "playing") {
    throw new IllegalCommandError(`cannot drill in ${state.status}`);
  }

  const piece = pieceAt(position, command.marshal);
  if (!piece || pieceColor(piece) !== mover) {
    throw new IllegalCommandError("not your piece");
  }
  if (!pieceVariant(variantAt(position, command.marshal)).commandsFodder) {
    throw new IllegalCommandError("that piece gives no orders");
  }

  // Validated **before** anything is written: a drill naming one bad square
  // turns nobody rather than the ones it reached first.
  // **Deduped, because this one crosses the socket** and lands in a log
  // replayed to every joiner. A `Set` keeps order, so refusals still name the
  // first bad square.
  const also = [...new Set(command.also ?? [])];
  for (const sq of also) {
    if (sq === command.marshal) {
      throw new IllegalCommandError("the Marshal is already turning");
    }
    if (!touches(command.marshal, sq)) {
      throw new IllegalCommandError("that piece is not beside the Marshal");
    }
    const held = pieceAt(position, sq);
    if (!held || pieceColor(held) !== mover) {
      throw new IllegalCommandError("not your piece to turn");
    }
    // The class table, not a rank list — `PIECE_CLASS` is where "fodder" lives.
    // Asked of the *square*, because a row may disagree with its rank.
    if (pieceClassAt(position, sq) !== CLASS_FODDER) {
      throw new IllegalCommandError("only fodder takes a Marshal's order");
    }
  }

  const next = cloneState(state);
  setFacing(next.position, command.marshal, command.facing);
  for (const sq of also) setFacing(next.position, sq, command.facing);

  // Consume the turn exactly as a move would: clocks, side to move, tick.
  next.position.halfmove++;
    spendMove(state, next, mover, false, false, command.marshal);
  return next;
}

/** Industry commands are free actions taken by the side to move, before its move. */


export function applyCommand(state: GameState, command: Command): GameState {
  // A state whose belts were written outside a command (a fixture, an old
  // save) is mirrored before anything reads it (§31 S2).
  state = withBeltMirror(state, state);
  let next = reduceCommand(state, command);
  // A destroyed enemy Monarch pays its pack (§31 R1), off the ids standing
  // before and after — so every door that kills a royal is one rule. Before
  // the stamp: a piece born this command has no id to have lost.
  if (next.run && next !== state && command.type !== "new-game") payForMonarchs(state, next, command);
  // Who moved and who was hurt, by body (§31 K5) — one diff for every door.
  if (next !== state && command.type !== "new-game") stampBodies(state.position, next.position);
  // **Belt parts stand in the structure mirror** (§31 S2), derived from the
  // industry's belt maps after every command — one writer for every door
  // that lays, lifts or wrecks a part, so movegen can never see stale track.
  next = withBeltMirror(state, next);
  // **Every piece has a name once a command is done** (`Position.pieceId`).
  // Here rather than at each creation site — mints, waves, raises, clones,
  // cards — so a new way to make a piece cannot forget one. Copied before it
  // is written: a reducer may hand back a position it shares with `state`.
  if (needsPieceIds(next.position)) {
    const position = clonePosition(next.position);
    stampPieceIds(position);
    next = { ...next, position };
  }
  // **A piece forged this turn has moved** (§33 T4), so no Empower network
  // grants it an extra move — Factory Flow aside, the forge still binds it.
  if (command.type === "forge" && next !== state) {
    const born = [...next.position.pieceId.values()].filter((id) => id >= state.position.nextPieceId);
    if (born.length > 0) {
      const position = clonePosition(next.position);
      for (const id of born) position.turnMoved.add(id);
      next = { ...next, position };
    }
  }
  if (next === state) return next;
  // **An Artisan Pillar lost** (§31 A6) — Pillar or Mark's bearer gone, by
  // any door: the bearer dies, the Pillar falls, frailty. Before `syncGear`,
  // which would drop the Mark where the bearer stood.
  if (command.type !== "new-game") next = settleArtisan(state, next);
  // **Equipment follows its bearer** (§31 A0) — dropped, picked up, issued
  // and pruned off one diff, after the ids it keys on are stamped.
  next = syncGear(next);
  // **Yield Not** (§31 A12): buildings' bulwark and the Shield's armor, off
  // the gear just synced. **My Deck Runith Over** (A11): filled tick counts
  // pay their card rewards.
  next = settleDefiance(state, next);
  next = payHouseOfCards(state, next);
  // **The artifact mirror** (§31 A6, A9, A11): wards, artisans, brass, which movegen
  // reads, derived from gear and structures after every command.
  if (artifactMirrorStale(next.position, next.industry)) {
    const position = next.position === state.position ? clonePosition(next.position) : next.position;
    syncArtifactMirror(position, next.industry);
    next = { ...next, position };
  }
  // **Resource overflow** (§33 I4): loose material past 20 a square spills
  // over the cross, after every door that drops, wrecks or spills.
  if (overflowStale(next.industry)) {
    const industry = next.industry === state.industry ? cloneIndustry(next.industry) : next.industry;
    settleOverflow(next.position, industry, next.seed, next.position.ply);
    next = { ...next, industry };
  }
  return next;
}

/** `next` with its belt mirror current, cloning a position shared with `state`. */
function withBeltMirror(state: GameState, next: GameState): GameState {
  if (!beltMirrorStale(next.position, next.industry)) return next;
  const position =
    next === state || next.position === state.position ? clonePosition(next.position) : next.position;
  syncBeltMirror(position, next.industry);
  return { ...next, position };
}

function reduceCommand(state: GameState, command: Command): GameState {
  // The factory and the run own their own cases (`industry/reduce.ts`,
  // `campaign/reduce.ts`). Asked in turn, and a `null` means "not mine" rather
  // than "unknown" — the exhaustiveness check for a genuinely unhandled type
  // is the `default` below, where the whole union is in scope.
  const industrial = reduceIndustry(state, command);
  if (industrial) return industrial;
  const campaign = reduceCampaign(state, command);
  if (campaign) return campaign;

  switch (command.type) {
    case "new-game": {
      // **Refused, never defaulted.** A build that does not have this map does
      // not have its board either, and opening the classic sixty-four instead
      // would be two clients replaying one log onto two different games — the
      // one failure the log-as-protocol cannot survive. An absent id is the
      // classic board and is not an unknown one.
      const map = boardMap(command.map);
      if (!map) throw new IllegalCommandError(`unknown map: ${command.map}`);
      // The map's rule again: a dial value this build does not know is a
      // client from another build, and defaulting it would be two clients
      // replaying one log onto two different games.
      for (const [, grade, , line] of command.stock ?? []) {
        const fault = depositFault(line ?? METAL, grade);
        if (fault) throw new IllegalCommandError(fault);
      }
      const ruleset = normalizeRuleset(command.ruleset);
      if (!ruleset) throw new IllegalCommandError("unknown ruleset");
      // The same rule a third time (§26 F13): a run mode this build does not
      // know is a client from another build, and reading it as normal would be
      // two clients replaying one log onto two different games.
      const mode = normalizeRunMode(command.mode);
      if (!mode) throw new IllegalCommandError(`unknown mode: ${String(command.mode)}`);
      // The map's ground and ore are *added* to whatever the command carries
      // rather than replacing it, so neither silently wins where a scenario
      // and a map are ever both given.
      const opened = newGame(
        command.fen,
        command.seed ?? state.seed,
        [...(command.heaps ?? []), ...map.heaps],
        command.movesPerTurn ?? 1,
        command.run,
        command.level,
        command.belts ?? [],
        command.stock ?? [],
        command.armies ?? [],
        command.kingKinds ?? [],
        command.demand ?? [],
        [...(command.cells ?? []), ...map.cells],
        command.alliances ?? [],
        command.objectives ?? [],
        command.metal ?? [],
        mode,
        [...(command.coal ?? []), ...map.coal]
      );
      // After the board, because a promotion square has to be on one: the
      // markers stand on the padding this map just laid. The owner goes on
      // beside the uses — an owned marker is one side's alone, which is what
      // stops the line behind your own army being a queen machine.
      for (const [sq, uses, owner] of map.promotion) {
        setPromotionUses(opened.position, sq, uses);
        setPromotionOwner(opened.position, sq, owner as Side | -1);
      }
      opened.position.promotionWalls = map.walls;
      // **A named map is a versus opening** (§31 Q13): its loose cobble is laid
      // only when the command names it — absent is the bare board every test,
      // perft, run and scenario opens on, hashing exactly as it always did.
      // Stored when named, so "New game" asks for the same opening again.
      if (command.map !== undefined) {
        opened.map = map.id;
        for (const [sq, units] of map.cobble) {
          if (!hasCell(opened.position, sq) || pieceAt(opened.position, sq) !== 0) continue;
          for (let i = 0; i < units; i++) dropOnGround(opened.industry, sq, COBBLE_UNIT);
        }
      }
      // Stored only off the default, so a command spelling the default out
      // opens a state indistinguishable from one that said nothing — which is
      // what keeps "with and without the field" hashing identically.
      if (!isDefaultRuleset(ruleset)) opened.ruleset = ruleset;
      // After the ground and the pieces, so `placeDeposits` can skip a square
      // somebody is already standing on.
      placeDeposits(opened.position, opened.industry, command.deposits ?? []);
      // Zones last (§31 D2): a zone's ore skips a tile something already
      // claimed, and its void never takes a square a piece stands on.
      if (map.zones) placeMapZones(opened.position, opened.industry, map.zones, opened.seed);
      // A generated map (§31 D3): its whole ground off the seed, by name.
      if (map.generate) layGeneratedMap(opened.position, opened.industry, MAP_GENS[map.generate], opened.seed);
      if (command.zones?.length) placeZones(opened.position, opened.industry, command.zones, opened.seed);
      return opened;
    }

    case "move":
      return applyMove(state, command);

    case "re-aim":
      return applyReAim(state, command);

    case "drill":
      return applyDrill(state, command);

    case "empower":
      return applyEmpower(state, command);

    case "lance":
      return applyLance(state, command);

    case "rally":
      return applyRally(state, command);

    case "pass": {
      const mover = requireOwnTurn(state);
      // Re-aim's rule, and for its reason: ending the turn moves nothing, so
      // it cannot answer a check — and a side that may sit in one is a side
      // check never becomes mate for. `requireOwnTurn` allows "check", so this
      // is the only thing standing between the two. Every action that can
      // leave the turn is king-safe, so a side that has spent its moves is
      // never here in check.
      if (state.status === "check") {
        throw new IllegalCommandError("cannot end the turn while in check");
      }

      const next = cloneState(state);
      // The only way a turn ends (§31 T2): the ticks it still owes, the clock,
      // the round, the turrets — `clock.ts`.
      endTurn(state, next, mover);
      return next;
    }

    default:
      // Whatever neither reducer claimed and no chess case matched.
      //
      // Deliberately *not* a `never` exhaustiveness check any more: the cases
      // live in three modules now, so no single switch sees the whole union
      // and the compiler cannot prove coverage from here. What replaces it is
      // `soloCommands.test.ts` walking the union at the server, plus this
      // throw — a command nobody handles is refused loudly rather than
      // silently returning the state unchanged.
      throw new IllegalCommandError(
        `unknown command: ${(command as { type: string }).type}`
      );
  }
}

/** Replay a whole log from scratch — save/load, reconnect and telemetry. */
export function replay(commands: readonly Command[]): GameState {
  let state = newGame();
  for (const command of commands) state = applyCommand(state, command);
  return state;
}

export const describeMove = moveToUci;
export { bufferCapacity, opposite };
